import { DateHelper } from "@bryntum/schedulerpro"
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro"
import { JobStatuses } from "@legacy/core/utils/enums"
import { useQuery } from "@tanstack/react-query"
import clsx from "clsx"

import useSlugExtractor from "@hooks/useSlugExtractor"
import useUser from "@hooks/useUser"

import formatDate from "@utils/formatDate"

import { Divider, Icon } from "@atoms"

import useJobTimelineViewStates from "@pages/Jobs/JobList/views/JobTimelineView/hooks/useJobTimelineViewStates"

import { JOB_ENDPOINTS } from "@endpoints/jobs"

import { JobPreviewCardProvider } from "./JobPreviewCard.context"
import styles from "./JobPreviewCard.module.scss"
import { JobPreviewCardProps } from "./JobPreviewCard.types"
import JobPreviewClient from "./components/JobPreviewClient/JobPreviewClient"
import JobPreviewCreatedInfo from "./components/JobPreviewCreatedInfo/JobPreviewCreatedInfo"
import JobPreviewDetails from "./components/JobPreviewDetails/JobPreviewDetails"
import JobPreviewId from "./components/JobPreviewId/JobPreviewId"
import JobPreviewLabels from "./components/JobPreviewLabels/JobPreviewLabels"
import JobPreviewLocationDetail from "./components/JobPreviewLocationDetail/JobPreviewLocationDetail"
import JobPreviewPriority from "./components/JobPreviewPriority/JobPreviewPriority"
import JobPreviewScheduleDate from "./components/JobPreviewScheduleDate/JobPreviewScheduleDate"
import JobPreviewServiceName from "./components/JobPreviewServiceName/JobPreviewServiceName"
import JobPreviewStatusBadge from "./components/JobPreviewStatusBadge/JobPreviewStatusBadge"
import JobPreviewTechniciansList from "./components/JobPreviewTechniciansList/JobPreviewTechniciansList"

export default function JobPreviewCard(props: JobPreviewCardProps) {
    const { jobId, initialData, isVisible = true, isPositionFixed = false } = props
    const { user } = useUser()
    const serviceCompanySlug = user?.service_company?.slug

    const [jobPreviewEndpoint] = useSlugExtractor([JOB_ENDPOINTS.PREVIEW], jobId)

    const { data: jobPreviewData, isFetching: isLoading } = useQuery<JobPreviewData, Error>({
        queryKey: ["jobPreview", jobId, serviceCompanySlug, jobPreviewEndpoint],
        queryFn: async () => {
            const response = await fetch(jobPreviewEndpoint)
            if (!response.ok) {
                throw new Error("Failed to fetch job preview data")
            }
            return response.json()
        },
        enabled: !!serviceCompanySlug && isVisible,
        placeholderData: initialData as JobPreviewData | undefined,
        staleTime: 60 * 1000, // 1 minute in milliseconds
    })

    const { todayInUserTimezone } = useJobTimelineViewStates()

    const isCurrentYear =
        new Date(jobPreviewData?.start_time || "").getFullYear() === new Date(todayInUserTimezone).getFullYear()

    let localCreatedAt: string | undefined

    if (jobPreviewData?.created_at) {
        const { localDateString: localDateStringCreatedAt } = formatDate({
            datetime: new Date(jobPreviewData.created_at),
            preferredTimezone: user?.service_company?.preferred_timezone,
            addDay: false,
            addFullDay: false,
            addYear: !isCurrentYear,
            short: true,
        })
        localCreatedAt = localDateStringCreatedAt
    }

    const isPastDue = jobPreviewData?.start_time
        ? DateHelper.isBefore(new Date(jobPreviewData?.start_time || ""), new Date(todayInUserTimezone))
        : false

    const isScheduled = jobPreviewData?.status === JobStatuses.scheduled
    const isOnHold = jobPreviewData?.status === JobStatuses.on_hold

    const techniciansList = jobPreviewData?.assigned_technicians
        ? jobPreviewData?.assigned_technicians?.length === 0
            ? [{ isUnknown: true, full_name: "Unassigned", avatar: "", gravatar: "" } satisfies AvatarUser]
            : jobPreviewData?.assigned_technicians
        : []

    return (
        <JobPreviewCardProvider value={{ isLoading }}>
            <div
                className={clsx(styles.base, {
                    [styles.danger]: isPastDue && isScheduled,
                    [styles.warning]: isPastDue && isOnHold,
                    [styles.visible]: isVisible,
                    [styles.positionFixed]: isPositionFixed,
                })}
            >
                <div className={styles.topSection}>
                    <div className={styles.jobAndStatus}>
                        <div className={styles.jobIdAndPriority}>
                            <JobPreviewId id={jobPreviewData?.custom_id || jobId} />
                            <JobPreviewPriority priority={jobPreviewData?.priority} />
                        </div>

                        <JobPreviewStatusBadge
                            statusLabel={jobPreviewData?.status_label || ""}
                            substatusLabel={jobPreviewData?.substatus_label || ""}
                            startTime={jobPreviewData?.start_time}
                            statusColorOverride={jobPreviewData?.status_color_override}
                        />
                    </div>

                    <div className={styles.clientLocationAndTechs}>
                        <div className={styles.jobNameAndClient}>
                            <JobPreviewServiceName service_name={jobPreviewData?.service_name} />
                            <JobPreviewClient
                                client={jobPreviewData?.client ? `for ${jobPreviewData.client}` : undefined}
                            />
                        </div>

                        <JobPreviewLocationDetail location={jobPreviewData?.location} />

                        <JobPreviewTechniciansList users={techniciansList} />
                    </div>
                </div>

                <div className={styles.scheduledForWrapper}>
                    {!(isPastDue && isScheduled) && <Divider />}
                    <JobPreviewScheduleDate
                        startTime={jobPreviewData?.start_time}
                        duration={jobPreviewData?.duration}
                        rescheduleRequired={isPastDue && isScheduled}
                    />
                    {!(isPastDue && isOnHold) && <Divider />}
                </div>

                {isPastDue && (isScheduled || isOnHold) && (
                    <div className={styles.pastDueWrapper}>
                        <div className={styles.pastDueWrapperInside}>
                            <Icon icon={icon({ name: "triangle-exclamation", style: "solid", family: "sharp" })} />

                            <span className={styles.pastDueLabel}>Reschedule required</span>
                        </div>
                    </div>
                )}
                <JobPreviewLabels labels={jobPreviewData?.labels} />
                <JobPreviewDetails details={jobPreviewData?.details} />

                <JobPreviewCreatedInfo createdAt={localCreatedAt} createdBy={jobPreviewData?.created_by} />
            </div>
        </JobPreviewCardProvider>
    )
}
